import React from 'react';
import { Skeleton, Tooltip } from '@mui/material';
import { Map } from '@mui/icons-material';

const formatCurrency = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n}`;
};

/** Choose intensity color based on exposure relative to max */
const heatColor = (val, max) => {
  if (max === 0) return 'bg-gray-100 text-gray-600';
  const ratio = val / max;
  if (ratio > 0.75) return 'bg-red-100 text-red-700 border-red-200';
  if (ratio > 0.50) return 'bg-orange-100 text-orange-700 border-orange-200';
  if (ratio > 0.25) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-emerald-100 text-emerald-700 border-emerald-200';
};

const RiskHeatmapSection = ({ data = [], loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <Skeleton variant="text" width={200} height={28} />
        <Skeleton variant="rounded" height={220} sx={{ mt: 2 }} />
      </div>
    );
  }

  const sorted = [...data].sort((a, b) => b.exposure - a.exposure);
  const maxExposure = sorted.length > 0 ? sorted[0].exposure : 0;

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <div className="flex items-center gap-2 mb-4">
        <Map sx={{ fontSize: 18, color: '#6366f1' }} />
        <h3 className="text-sm font-semibold text-gray-700">Risk Heatmap by District</h3>
        <span className="ml-auto text-xs text-gray-400">{sorted.length} districts</span>
      </div>

      {sorted.length === 0 ? (
        <div className="flex items-center justify-center py-12 text-sm text-gray-400">
          No district data available with current filters
        </div>
      ) : (
        <>
          {/* Legend */}
          <div className="flex items-center gap-3 mb-3 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-emerald-200" /> Low
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-amber-200" /> Medium
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-orange-200" /> High
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-red-200" /> Critical
            </span>
          </div>

          {/* Grid of district tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[320px] overflow-y-auto pr-1">
            {sorted.map((d) => {
              const claimRatio = d.policies > 0 ? ((d.claims / d.policies) * 100).toFixed(0) : 0;
              return (
                <Tooltip
                  key={d.district}
                  title={`${d.district}: ${d.policies} policies, ${d.claims} claims, Exposure: ${formatCurrency(d.exposure)}`}
                  arrow
                >
                  <div
                    className={`rounded-xl p-3 border cursor-default transition-all hover:scale-[1.02] ${heatColor(d.exposure, maxExposure)}`}
                  >
                    <p className="text-xs font-bold truncate">{d.district}</p>
                    <p className="text-lg font-bold mt-1">{formatCurrency(d.exposure)}</p>
                    <div className="flex items-center gap-2 mt-1 text-[10px] opacity-80">
                      <span>{d.policies} pol</span>
                      <span>·</span>
                      <span>{d.claims} clm</span>
                      <span>·</span>
                      <span>{claimRatio}% ratio</span>
                    </div>
                  </div>
                </Tooltip>
              );
            })}
          </div>

          {/* Summary bar */}
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
            <span>
              Total Exposure: <strong className="text-gray-700">{formatCurrency(sorted.reduce((s, d) => s + d.exposure, 0))}</strong>
            </span>
            <span>
              Total Claims: <strong className="text-gray-700">{sorted.reduce((s, d) => s + d.claims, 0)}</strong>
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default RiskHeatmapSection;
